<template>
  <div>
    <!-- Page header -->
    <b-row class="mb-2">
      <b-col>
        <h4 class="mb-0">
          <feather-icon
            icon="UploadCloudIcon"
            size="20"
            class="mr-50 text-primary"
          />
          {{ $t('modules.products.import.page_title') }}
        </h4>
        <p class="text-muted mt-25 mb-0">
          {{ $t('modules.products.import.page_description') }}
        </p>
      </b-col>
    </b-row>

    <!-- Wizard steps indicator -->
    <b-card
      no-body
      class="mb-2"
    >
      <b-card-body class="py-1">
        <div class="wizard-steps d-flex align-items-center justify-content-between">
          <div
            v-for="(step, index) in steps"
            :key="step.key"
            class="wizard-step d-flex align-items-center"
            :class="{ 'flex-grow-1': index < steps.length - 1 }"
          >
            <!-- Step bubble -->
            <div
              class="step-bubble d-flex align-items-center justify-content-center rounded-circle"
              :class="getStepBubbleClass(index)"
            >
              <feather-icon
                v-if="index < currentStep"
                icon="CheckIcon"
                size="14"
              />
              <span
                v-else
                class="small font-weight-bold"
              >{{ index + 1 }}</span>
            </div>

            <!-- Step label -->
            <span
              class="step-label ml-50 d-none d-md-inline"
              :class="currentStep === index ? 'text-primary font-weight-bold' : 'text-muted'"
            >
              {{ $t(`modules.products.import.steps.${step.key}.tab_label`) }}
            </span>

            <!-- Connector -->
            <div
              v-if="index < steps.length - 1"
              class="step-connector flex-grow-1 mx-1"
              :class="index < currentStep ? 'bg-primary' : 'bg-light-secondary'"
            />
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Step content -->
    <step1-file-upload
      v-if="currentStep === 0"
      :has-header-row.sync="hasHeaderRow"
      :is-loading="isUploadLoading"
      :error="uploadError"
      @next="onStep1Next"
    />

    <step2-column-mapping
      v-else-if="currentStep === 1"
      v-model="columnMapping"
      :columns="parsedColumns"
      :is-loading="isPreviewLoading"
      @next="onStep2Next"
      @back="goToStep(0)"
    />

    <step3-preview
      v-else-if="currentStep === 2"
      :preview-rows="previewRows"
      :total-rows="totalRows"
      :column-mapping="columnMapping"
      :is-loading="isPreviewLoading"
      :is-importing="isImporting"
      @back="goToStep(1)"
      @import="onClickImport"
    />

    <!-- Importing overlay -->
    <b-overlay
      :show="isImporting"
      no-wrap
      rounded
    >
      <template #overlay>
        <div class="text-center">
          <b-spinner variant="primary" />
          <p class="mt-1 text-primary font-weight-bold">
            {{ $t('modules.products.import.importing_in_progress') }}
          </p>
          <p class="text-muted small">
            {{ $t('modules.products.import.do_not_close') }}
          </p>
        </div>
      </template>
    </b-overlay>
  </div>
</template>

<script>
import store from '@/store'
import { ref, onUnmounted } from 'vue'
import { useRouter } from '@core/utils/utils'
import {
  BCard, BCardBody, BCol, BRow, BSpinner, BOverlay,
} from 'bootstrap-vue'
import { toast, commonRequestErrorsHandler } from '@core/utils/utils'
import productImportStoreModule from '../productImportStoreModule'
import Step1FileUpload from './steps/Step1FileUpload.vue'
import Step2ColumnMapping from './steps/Step2ColumnMapping.vue'
import Step3Preview from './steps/Step3Preview.vue'

const IMPORT_STORE = 'product_imports'

const STEPS = [
  { key: 'upload' },
  { key: 'mapping' },
  { key: 'preview' },
]

export default {
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BSpinner,
    BOverlay,
    Step1FileUpload,
    Step2ColumnMapping,
    Step3Preview,
  },

  setup() {
    const { router } = useRouter()

    if (!store.hasModule(IMPORT_STORE)) store.registerModule(IMPORT_STORE, productImportStoreModule)
    onUnmounted(() => {
      if (store.hasModule(IMPORT_STORE)) store.unregisterModule(IMPORT_STORE)
    })

    const steps = STEPS
    const currentStep = ref(0)

    // Step 1 state
    const hasHeaderRow = ref(true)
    const isUploadLoading = ref(false)
    const uploadError = ref(null)

    // Step 2 state
    const importId = ref(null)
    const parsedColumns = ref([])
    const columnMapping = ref({})

    // Step 3 state
    const isPreviewLoading = ref(false)
    const previewRows = ref([])
    const totalRows = ref(0)

    // Import state
    const isImporting = ref(false)
    const activeOperationId = ref(null)

    const goToStep = step => {
      currentStep.value = step
    }

    const getStepBubbleClass = index => {
      if (index < currentStep.value) return 'bg-primary text-white'
      if (index === currentStep.value) return 'bg-primary text-white'
      return 'bg-light-secondary text-muted'
    }

    // Step 1 → Upload file, get column headers
    const onStep1Next = ({ file, hasHeaderRow: headerRow }) => {
      hasHeaderRow.value = headerRow
      isUploadLoading.value = true
      uploadError.value = null

      store.dispatch(`${IMPORT_STORE}/uploadFile`, { file, hasHeaderRow: headerRow })
        .then(data => {
          importId.value = data.import_id
          parsedColumns.value = data.columns || []
          columnMapping.value = {}
          currentStep.value = 1
        })
        .catch(e => {
          uploadError.value = e?.response?.data?.message || 'Failed to parse the file. Please check the format and try again.'
          commonRequestErrorsHandler(e, false)
        })
        .finally(() => {
          isUploadLoading.value = false
        })
    }

    // Step 2 → Fetch preview
    const onStep2Next = mappingValues => {
      columnMapping.value = mappingValues
      isPreviewLoading.value = true

      store.dispatch(`${IMPORT_STORE}/previewImport`, {
        importId: importId.value,
        columnMapping: mappingValues,
        hasHeaderRow: hasHeaderRow.value,
      })
        .then(data => {
          previewRows.value = data.preview_rows || []
          totalRows.value = data.total_rows || 0
          currentStep.value = 2
        })
        .catch(e => {
          commonRequestErrorsHandler(e)
        })
        .finally(() => {
          isPreviewLoading.value = false
        })
    }

    // Poll until the async import job finishes
    const pollOperationStatus = () => {
      store.dispatch(`${IMPORT_STORE}/fetchOperationStatus`, activeOperationId.value)
        .then(data => {
          if (data.status === 'pending') {
            setTimeout(pollOperationStatus, 3000)
          } else if (data.status === 'finished') {
            isImporting.value = false
            const imported = data.imported_count ?? totalRows.value
            toast(
              'success',
              'Import Successful',
              `${imported} products were imported successfully.`,
              'CheckCircleIcon',
            )
            router.push({ name: 'products.list' })
          } else {
            isImporting.value = false
            const errorMsg = data.errors?.length
              ? data.errors.slice(0, 3).join(', ')
              : 'The import job failed. Please check your file and try again.'
            toast('danger', 'Import Failed', errorMsg, 'AlertTriangleIcon')
          }
        })
        .catch(e => {
          isImporting.value = false
          commonRequestErrorsHandler(e)
        })
    }

    // Step 3 → Kick off import
    const onClickImport = () => {
      isImporting.value = true

      store.dispatch(`${IMPORT_STORE}/startImport`, {
        importId: importId.value,
        columnMapping: columnMapping.value,
        hasHeaderRow: hasHeaderRow.value,
      })
        .then(data => {
          activeOperationId.value = data.operation_id
          pollOperationStatus()
        })
        .catch(e => {
          isImporting.value = false
          commonRequestErrorsHandler(e)
        })
    }

    return {
      steps,
      currentStep,
      hasHeaderRow,
      isUploadLoading,
      uploadError,
      parsedColumns,
      columnMapping,
      isPreviewLoading,
      previewRows,
      totalRows,
      isImporting,
      goToStep,
      getStepBubbleClass,
      onStep1Next,
      onStep2Next,
      onClickImport,
    }
  },
}
</script>

<style lang="scss" scoped>
.wizard-steps {
  .step-bubble {
    width: 28px;
    height: 28px;
    min-width: 28px;
    font-size: 0.8rem;
    transition: background-color 0.2s ease;
  }

  .step-connector {
    height: 2px;
    border-radius: 1px;
    transition: background-color 0.2s ease;
  }

  .step-label {
    font-size: 0.9rem;
    white-space: nowrap;
  }
}
</style>
